import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { List, MapPin, Plus, EyeOff, Eye, RotateCcw } from "lucide-react";
import {
  deletePicklistItem,
  listAllPicklists,
  listClients,
  listSitesLite,
  upsertPicklistItem,
} from "@/lib/api";
import FieldPlacementEditor from "@/components/FieldPlacementEditor";

const KEYS = [
  { value: "vehicle_type",     label: "Vehicle types" },
  { value: "goods_category",   label: "Goods categories" },
  { value: "ppe",              label: "PPE catalogue" },
  { value: "pallet_type",      label: "Pallet brands (Chep, Loscam, …)" },
  { value: "load_rule",        label: "Load rules" },
  { value: "restraint_method", label: "Restraint methods" },
  { value: "temp_set",         label: "Temperature set" },
  { value: "issue_type",       label: "Issue / NCR types" },
  { value: "cold_chain",       label: "Cold chain" },
];

export default function Picklists() {
  const [clients, setClients] = useState([]);
  const [sites, setSites]     = useState([]);  // for scope picker
  const [scopingRow, setScopingRow] = useState(null);  // row being scoped to sites
  const [clientId, setClientId] = useState("__global__");
  const [key, setKey] = useState("vehicle_type");
  const [rows, setRows] = useState([]);
  const [tick, setTick] = useState(0);
  const refresh = () => setTick((k) => k + 1);
  const [adding, setAdding] = useState({ value: "", label: "", sort_order: 0 });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await listClients();
      if (cancelled) return;
      setClients(data || []);
    })();
    return () => { cancelled = true; };
  }, []);

  // Load this tenant's sites whenever clientId changes (used by Scope dialog).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (clientId === "__global__") { setSites([]); return; }
      const { data } = await listSitesLite();
      if (cancelled) return;
      setSites((data || []).filter((s) => s.client_id === clientId));
    })();
    return () => { cancelled = true; };
  }, [clientId]);

  const saveScope = async (siteIds) => {
    if (!scopingRow) return;
    const { error } = await upsertPicklistItem({
      id: scopingRow.id,
      scope_site_ids: siteIds.length ? siteIds : null,
    });
    if (error) { toast.error(error.message); return; }
    toast.success(siteIds.length ? `Scoped to ${siteIds.length} site(s).` : "Scope cleared — all sites.");
    setScopingRow(null);
    refresh();
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const cid = clientId === "__global__" ? null : clientId;
      const { data, error } = await listAllPicklists(cid);
      if (cancelled) return;
      if (error) toast.error(error.message);
      setRows((data || []).filter((r) => r.picklist_key === key));
    })();
    return () => { cancelled = true; };
  }, [clientId, key, tick]);

  const onAdd = async () => {
    if (!adding.value) {
      toast.error("Value required.");
      return;
    }
    const { error } = await upsertPicklistItem({
      client_id: clientId === "__global__" ? null : clientId,
      picklist_key: key,
      value: adding.value,
      label: adding.label || adding.value,
      sort_order: parseInt(adding.sort_order, 10) || 0,
      is_active: true,
    });
    if (error) toast.error(error.message);
    else {
      toast.success("Added.");
      setAdding({ value: "", label: "", sort_order: 0 });
      refresh();
    }
  };

  const onToggle = async (row) => {
    const { data, error } = await upsertPicklistItem({ id: row.id, is_active: !row.is_active });
    if (error) {
      toast.error(error.message);
      return;
    }
    // Belt-and-braces: detect the RLS silent-deny case where update
    // returned success with zero affected rows (e.g. when the row's
    // policy doesn't permit this user). If we got nothing back, refresh
    // to confirm whether the value actually changed.
    if (!data || (Array.isArray(data) && data.length === 0)) {
      toast.warning("Update returned no rows — refreshing to confirm.");
    }
    refresh();
  };

  // Soft-hide / restore. Three modes depending on row scope vs current scope:
  //   A. tenant override row → hard delete (restore default).
  //   B. global row viewed under tenant scope → create tenant override
  //      with is_active=false (hide for this tenant only).
  //   C. global row viewed as Global → toggle is_active on the global row.
  const onSoftHide = async (row) => {
    // (A) Tenant override row — restore default by deleting the override.
    if (row.client_id) {
      if (!window.confirm(`Restore "${row.label || row.value}" to the global default?`)) return;
      const { error } = await deletePicklistItem(row.id);
      if (error) toast.error(error.message);
      else { toast.success("Override removed. Now follows the global default."); refresh(); }
      return;
    }
    // (B) Global row, viewing a specific tenant — create a hide-override.
    if (clientId !== "__global__") {
      const { error } = await upsertPicklistItem({
        client_id: clientId,
        picklist_key: row.picklist_key,
        value: row.value,
        label: row.label,
        sort_order: row.sort_order,
        is_active: false,
      });
      if (error) toast.error(error.message);
      else { toast.success(`Hidden for this tenant only.`); refresh(); }
      return;
    }
    // (C) Global row, viewing as Global — toggle is_active globally.
    onToggle(row);
  };

  return (
    <div data-testid="picklists-page" className="space-y-4">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <List className="h-6 w-6" /> Picklists
          </h1>
          <p className="text-sm text-slate-500">
            Editable per-client dropdowns. Global rows (no client) show in every tenant; client-specific rows override.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <div className="space-y-1.5">
            <div className="text-[11px] uppercase tracking-wider text-slate-500">Picklist</div>
            <Select value={key} onValueChange={setKey}>
              <SelectTrigger className="w-56" data-testid="pl-key"><SelectValue /></SelectTrigger>
              <SelectContent>
                {KEYS.map((k) => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <div className="text-[11px] uppercase tracking-wider text-slate-500">Scope</div>
            <Select value={clientId} onValueChange={setClientId}>
              <SelectTrigger className="w-60" data-testid="pl-scope"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__global__">Global (all tenants)</SelectItem>
                {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Add new</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <Input placeholder="Value (code)"  value={adding.value} onChange={(e) => setAdding({ ...adding, value: e.target.value })} data-testid="pl-add-value" />
          <Input placeholder="Label (shown)" value={adding.label} onChange={(e) => setAdding({ ...adding, label: e.target.value })} data-testid="pl-add-label" />
          <Input type="number" placeholder="Sort" value={adding.sort_order} onChange={(e) => setAdding({ ...adding, sort_order: e.target.value })} data-testid="pl-add-sort" />
          <Button onClick={onAdd} data-testid="pl-add-submit"><Plus className="h-4 w-4 mr-1.5" />Add</Button>
        </CardContent>
      </Card>

      <FieldPlacementEditor clientId={clientId} />

      <Card>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase text-slate-500 border-b">
              <th className="px-4 py-2">Value</th><th>Label</th><th>Sort</th><th>Scope</th><th>Active</th><th className="w-12" />
            </tr></thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={6} className="py-10 text-center text-slate-400">No items.</td></tr>
              ) : rows.map((r) => (
                <tr key={r.id} className="border-t" data-testid={`pl-row-${r.id}`}>
                  <td className="px-4 py-2 font-mono text-xs">{r.value}</td>
                  <td>{r.label}</td>
                  <td className="tabular-nums">{r.sort_order}</td>
                  <td>
                    <Badge variant="secondary" className={r.client_id ? "bg-blue-100 text-blue-700" : "bg-slate-100"}>
                      {r.client_id ? "Client" : "Global"}
                    </Badge>
                    {r.client_id && (
                      <button
                        type="button"
                        onClick={() => setScopingRow(r)}
                        className={`ml-1 text-[11px] inline-flex items-center gap-1 px-1.5 py-0.5 rounded border ${
                          (r.scope_site_ids?.length || 0) > 0
                            ? "bg-amber-50 text-amber-800 border-amber-300"
                            : "bg-slate-50 text-slate-500 border-slate-200"
                        }`}
                        data-testid={`pl-scope-edit-${r.id}`}
                        title="Click to restrict this option to specific sites"
                      >
                        <MapPin className="h-3 w-3" />
                        {(r.scope_site_ids?.length || 0) > 0
                          ? `${r.scope_site_ids.length} site${r.scope_site_ids.length === 1 ? "" : "s"}`
                          : "All sites"}
                      </button>
                    )}
                  </td>
                  <td><Switch checked={r.is_active} onCheckedChange={() => onToggle(r)} data-testid={`pl-toggle-${r.id}`} /></td>
                  <td>
                    <SoftHideButton row={r} scopeIsGlobal={clientId === "__global__"} onClick={() => onSoftHide(r)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* ── Site scope dialog ───────────────────────────────────────── */}
      {scopingRow && (
        <ScopeDialog
          key={scopingRow.id}
          row={scopingRow}
          sites={sites}
          onClose={() => setScopingRow(null)}
          onSave={saveScope}
        />
      )}
    </div>
  );
}

function SoftHideButton({ row, scopeIsGlobal, onClick }) {
  // Pick the right icon + tooltip based on the row's state.
  let Icon = EyeOff;
  let tip = "Hide for this tenant";
  if (row.client_id) {
    Icon = RotateCcw;
    tip = "Restore default (remove tenant override)";
  } else if (scopeIsGlobal) {
    Icon = row.is_active ? EyeOff : Eye;
    tip = row.is_active ? "Deactivate globally" : "Reactivate globally";
  }
  return (
    <Button
      size="icon"
      variant="ghost"
      onClick={onClick}
      data-testid={`pl-hide-${row.id}`}
      title={tip}
    >
      <Icon className="h-4 w-4 text-slate-500" />
    </Button>
  );
}

function ScopeDialog({ row, sites, onClose, onSave }) {
  const [selected, setSelected] = useState(row.scope_site_ids || []);
  const toggle = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  return (
    <Dialog open={!!row} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="pl-scope-dialog">
        <DialogHeader>
          <DialogTitle>Restrict &quot;{row.label || row.value}&quot; to sites</DialogTitle>
        </DialogHeader>
        <div className="space-y-2 max-h-[55vh] overflow-y-auto">
          <p className="text-xs text-slate-500">
            Tick the sites where this option should appear. Leave all unticked to show on every site (default).
          </p>
          {sites.length === 0 ? (
            <p className="text-sm text-slate-400 italic">No sites in this tenant yet.</p>
          ) : sites.map((s) => (
            <label key={s.id} className="flex items-center gap-2 text-sm py-1 border-b last:border-0">
              <Checkbox
                checked={selected.includes(s.id)}
                onCheckedChange={() => toggle(s.id)}
                data-testid={`pl-scope-site-${s.id}`}
              />
              <span>{s.name}</span>
              {s.code && <span className="text-xs text-slate-400 font-mono">({s.code})</span>}
            </label>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="ghost" onClick={() => onSave([])} data-testid="pl-scope-clear">All sites</Button>
          <Button onClick={() => onSave(selected)} data-testid="pl-scope-save">
            Save scope
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
